import { intervalToDuration } from 'date-fns';
import Link from 'next/link';

import { SymbolTopTraderOpenPosition } from '@flipster/apis/v1/community/types';
import { OrderSideBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import CrownIcon from '@flipster/react/icon/generated/CrownIcon';
import UserCircleIcon from '@flipster/react/icon/generated/UserCircleIcon';
import { css } from '@flipster/styled-system/css';
import { divider, hstack, stack } from '@flipster/styled-system/patterns';
import { timestampToDate } from '@flipster/utils/datetime';
import { getOrdinalNumber } from '@flipster/utils/num';

interface SymbolOpenPositionCardProps {
  position: SymbolTopTraderOpenPosition;
}

export const SymbolOpenPositionCard = ({ position }: SymbolOpenPositionCardProps) => {
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { displayShortName, pricePrecision } = instrumentsKeyBySymbol?.[position.symbol] ?? {};
  const ordinal = getOrdinalNumber(position.symbolWeeklyRank);
  const openedMinutes = (() => {
    const { minutes } = intervalToDuration({ start: timestampToDate(position.openedAt), end: Date.now() });
    return (minutes ?? 0) + 1;
  })();
  return (
    <div
      className={stack({
        px: 12,
        py: 8,
        gap: 8,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
        bg: 'background.default.level-1',
        rounded: 4,
      })}
    >
      <SymbolDisplay
        css={{ w: 'fit-content' }}
        symbol={position.symbol}
        icon={<SymbolDisplay.Icon size={20} space={4} />}
        mainLabel={
          <SymbolDisplay.Label value='shortName' css={hstack.raw({ textStyle: 'Text/M/Strong', gap: 4 })}>
            <OrderSideBadge orderSide={position.side} />
          </SymbolDisplay.Label>
        }
      />
      <div className={hstack({ justifyContent: 'space-between' })}>
        <p className={hstack({ gap: 4 })}>
          <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Entry_price' />
          </span>
          <Formatted.Number
            value={position.entryPrice}
            toFixed
            precision={pricePrecision}
            css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' }}
          />
        </p>
        <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
          <FormattedMessage
            id='Autogen_common.r251126.s_Open_openedMinutes_m_ago'
            values={{
              openedMinutes: openedMinutes,
            }}
          />
        </p>
      </div>
      <hr
        className={divider({
          orientation: 'horizontal',
          color: 'border.default.level-1',
        })}
      />
      <div className={hstack({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
        <Link
          className={hstack({ gap: 4, minW: 0, color: 'content.default.level-2' })}
          href={`/user/profile/${position.userProfileId}`}
        >
          <UserCircleIcon size={16} className={css({ flexShrink: 0 })} />
          <p className={css({ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })}>
            {position.nickname}
          </p>
        </Link>
        <p className={css({ ml: 4, mr: 2 })}>•</p>
        <div className={hstack({ gap: 2, flexShrink: 0 })}>
          <CrownIcon size={16} />
          <p>
            {displayShortName} P&L {ordinal.number}
            {ordinal.suffix}
          </p>
        </div>
      </div>
    </div>
  );
};
